window.defaultAnimalPdpPublish =
  window.defaultAnimalPdpPublish ||
  ((name, payload = {}) => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      const analytics = window.Shopify?.analytics;
      if (typeof privacy?.analyticsProcessingAllowed !== 'function') return;
      if (privacy.analyticsProcessingAllowed() !== true || typeof analytics?.publish !== 'function') return;
      const publishResult = analytics.publish(`default_animal:${name}`, payload);
      if (publishResult && typeof publishResult.catch === 'function') {
        publishResult.catch(() => {});
      }
    } catch {
      return;
    }
  });

if (!customElements.get('product-offers')) {
  customElements.define(
    'product-offers',
    class ProductOffers extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        this.quantityInput = document.getElementById(this.dataset.quantityInput);
        this.options = Array.from(this.querySelectorAll('[data-offer-quantity]'));
        this.status = this.querySelector('[data-offer-status]');
        if (!this.quantityInput || this.options.length !== 3) return;

        this.quantityForm = this.quantityInput.closest('.product-form__quantity');

        for (const option of this.options) {
          option.addEventListener('change', () => this.select(option, true), { signal: this.controller.signal });
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data?.variant || String(data.sectionId) !== String(this.closest('product-info')?.dataset.section)) return;
            this.dataset.variantId = String(data.variant.id);
            this.dataset.price = String(data.variant.price);
            this.refresh();
          });
        }

        this.refresh();
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.unsubscribeVariantChange?.();
        this.quantityForm?.classList.remove('pdp-quantity--offers-active');
      }

      select(option, publishSelection = false) {
        if (option.disabled) return;
        const quantity = Number.parseInt(option.dataset.offerQuantity, 10);
        this.quantityInput.value = String(quantity);
        this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        if (this.status) {
          this.status.textContent = option.closest('.pdp-offers__option')?.innerText.trim() || '';
        }

        if (publishSelection) {
          const detail = {
            productId: this.dataset.productId,
            variantId: this.dataset.variantId,
            quantity,
            discountPercent: Number.parseInt(option.dataset.offerDiscount, 10) || 0,
          };
          this.dispatchEvent(new CustomEvent('pdp:offer-selected', { bubbles: true, detail }));
          window.defaultAnimalPdpPublish('offer_selected', detail);
        }
      }

      refresh() {
        const price = Number.parseInt(this.dataset.price, 10);
        if (!Number.isFinite(price)) return;

        let selected = this.options.find((option) => option.checked);
        for (const option of this.options) {
          const quantity = Number.parseInt(option.dataset.offerQuantity, 10);
          option.disabled = !this.isQuantityValid(quantity);
          this.renderPrice(option, price, quantity);
        }

        const firstAvailable = this.options.find((option) => !option.disabled);
        if (!firstAvailable) {
          this.removeAttribute('data-upgraded');
          this.quantityForm?.classList.remove('pdp-quantity--offers-active');
          return;
        }

        this.setAttribute('data-upgraded', '');
        this.quantityForm?.classList.add('pdp-quantity--offers-active');

        if (!selected || selected.disabled) {
          selected = firstAvailable;
          if (selected) selected.checked = true;
        }

        if (selected) this.select(selected);
      }

      isQuantityValid(quantity) {
        const ruleMinimum = Number.parseInt(this.quantityInput.dataset.min, 10) || 1;
        const ruleMaximum = Number.parseInt(this.quantityInput.dataset.max, 10);
        const cartQuantity = Number.parseInt(this.quantityInput.dataset.cartQuantity, 10) || 0;
        const increment = Number.parseInt(this.quantityInput.step, 10) || 1;
        const maximum = Number.isFinite(ruleMaximum) ? ruleMaximum - cartQuantity : null;
        let minimum = ruleMinimum;
        if (maximum !== null) minimum = Math.min(minimum, maximum);
        if (cartQuantity >= ruleMinimum) minimum = Math.min(minimum, increment);
        if (quantity < minimum) return false;
        if (maximum !== null && quantity > maximum) return false;
        return (quantity - minimum) % increment === 0;
      }

      renderPrice(option, price, quantity) {
        const discount = Number.parseInt(option.dataset.offerDiscount, 10) || 0;
        const regularTotal = price * quantity;
        // Mirror Shopify's own arithmetic: the discount is floored per unit and
        // then multiplied, not rounded on the order total. Rounding the total
        // showed a saving two or three cents larger than checkout applies.
        const unitSaving = Math.floor((price * discount) / 100);
        const savings = unitSaving * quantity;
        const total = regularTotal - savings;
        const unit = price - unitSaving;
        const card = option.nextElementSibling;
        if (!card) return;

        const totalNode = card.querySelector('[data-offer-total]');
        const unitNode = card.querySelector('[data-offer-unit]');
        const savingNode = card.querySelector('[data-offer-saving]');
        if (totalNode) totalNode.textContent = this.formatMoney(total);
        if (unitNode) {
          unitNode.textContent = (this.dataset.perItemTemplate || '[price]').replace('[price]', this.formatMoney(unit));
        }
        if (savingNode) {
          // Tier one has nothing to save, so the node stays empty rather than
          // rendering a "Save 0,00" that reads as a broken promise.
          if (savings > 0) {
            savingNode.textContent = (this.dataset.saveTemplate || '[amount]').replace('[amount]', this.formatMoney(savings));
            savingNode.hidden = false;
          } else {
            savingNode.textContent = '';
            savingNode.hidden = true;
          }
        }
      }

      formatMoney(cents) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: this.dataset.currency,
          }).format(cents / 100);
        } catch {
          return (cents / 100).toFixed(2);
        }
      }
    }
  );
}

if (!customElements.get('product-size-guide')) {
  customElements.define(
    'product-size-guide',
    class ProductSizeGuide extends HTMLElement {
      connectedCallback() {
        // The chart now lives inside a "Good to know" accordion panel, so the
        // native <details> owns opening and closing. This element only reports
        // the open, and does nothing when the chart is rendered outside one.
        this.panel = this.closest('details');
        if (!this.panel) return;

        this.controller = new AbortController();
        this.panel.addEventListener(
          'toggle',
          () => {
            if (!this.panel.open) return;
            window.defaultAnimalPdpPublish('size_guide_opened', { productId: this.dataset.productId });
          },
          { signal: this.controller.signal }
        );
      }

      disconnectedCallback() {
        this.controller?.abort();
      }
    }
  );
}

if (!customElements.get('product-review-slider')) {
  customElements.define(
    'product-review-slider',
    class ProductReviewSlider extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        this.track = this.querySelector('[data-review-track]');
        if (!this.track) return;

        this.querySelector('[data-review-previous]')?.addEventListener('click', () => this.move(-1), {
          signal: this.controller.signal,
        });
        this.querySelector('[data-review-next]')?.addEventListener('click', () => this.move(1), {
          signal: this.controller.signal,
        });
      }

      disconnectedCallback() {
        this.controller?.abort();
      }

      move(direction) {
        const review = this.track.querySelector('.pdp-reviews__review');
        if (!review) return;
        const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this.track.scrollBy({
          left: direction * (review.getBoundingClientRect().width + gap),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
        window.defaultAnimalPdpPublish('review_navigated', {
          productId: this.dataset.productId,
          direction: direction < 0 ? 'previous' : 'next',
        });
      }
    }
  );
}

if (!customElements.get('product-faq')) {
  customElements.define(
    'product-faq',
    class ProductFaq extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        for (const item of this.querySelectorAll('details[data-faq-index]')) {
          item.addEventListener(
            'toggle',
            () => {
              if (!item.open) return;
              window.defaultAnimalPdpPublish('product_faq_opened', {
                productId: this.dataset.productId,
                questionIndex: Number.parseInt(item.dataset.faqIndex, 10),
              });
            },
            { signal: this.controller.signal }
          );
        }
      }

      disconnectedCallback() {
        this.controller?.abort();
      }
    }
  );
}
